import React from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import { cn } from "@/lib/utils";

/**
 * SessionCard — used on the Home page and History grid.
 *
 * Phase 9 colour tokens:
 *   - Score tier:  85+ status-success | 70+ primary | 50+ status-warning | <50 destructive
 *   - Status pill: draft=inactive | active=success | completed=warning | reviewed=primary | failed=error
 *
 * Dark-mode safe via token-driven `bg-status-*` + `text-status-*` from Tailwind config.
 */
const scoreTone = (score) => {
    if (score == null) return "border-border bg-secondary text-foreground/80";
    if (score >= 85) return "border-status-success/30 bg-status-success/10 text-status-success";
    if (score >= 70) return "border-primary/30 bg-primary/10 text-primary";
    if (score >= 50) return "border-status-warning/30 bg-status-warning/10 text-status-warning";
    return "border-destructive/30 bg-destructive/10 text-destructive";
};

const STATUS_TONE = {
    draft:     "border-status-inactive/30 bg-status-inactive/10 text-status-inactive",
    active:    "border-status-success/30 bg-status-success/10 text-status-success",
    completed: "border-status-warning/30 bg-status-warning/10 text-status-warning",
    reviewed:  "border-primary/30 bg-primary/10 text-primary",
    failed:    "border-status-error/30 bg-status-error/10 text-status-error",
};

const STATUS_LABEL = {
    draft: "Draft",
    active: "In progress",
    completed: "Completed",
    reviewed: "Reviewed",
    failed: "Review failed",
};

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
    } catch {
        return "";
    }
};

export const SessionCard = ({ session }) => {
    const linkTo =
        session.status === "reviewed" || session.status === "completed" || session.status === "failed"
            ? `/practice/review/${session.id}`
            : `/practice/session/${session.id}`;

    return (
        <Link
            to={linkTo}
            className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
            data-testid={`session-card-${session.id}`}
        >
            <Card className="overflow-hidden border-border/80 shadow-sm transition hover:border-primary/30 hover:shadow-md">
                <div className={cn("relative", session.is_couple ? "aspect-[24/14]" : "aspect-square")}>
                    <AvatarPortrait
                        name={session.avatar_name}
                        imageUrl={session.avatar_image_url}
                        aspect={session.is_couple ? "wide" : "square"}
                        rounded="rounded-none"
                    />
                    <div className="absolute right-2 top-2 flex flex-col items-end gap-1.5">
                        <span
                            className={cn(
                                "rounded-full border px-2 py-0.5 text-[10px] font-medium backdrop-blur-sm",
                                STATUS_TONE[session.status] || STATUS_TONE.draft
                            )}
                            data-testid={`session-card-status-${session.id}`}
                        >
                            {STATUS_LABEL[session.status] || session.status}
                        </span>
                        {session.overall_score != null && (
                            <span
                                className={cn(
                                    "rounded-full border px-2 py-0.5 text-[10px] font-semibold tabular-nums backdrop-blur-sm",
                                    scoreTone(session.overall_score)
                                )}
                                data-testid={`session-card-score-${session.id}`}
                            >
                                {session.overall_score}
                            </span>
                        )}
                    </div>
                </div>
                <CardContent className="space-y-2 p-4">
                    <h3 className="truncate font-semibold text-foreground" data-testid={`session-card-name-${session.id}`}>
                        {session.avatar_name}
                    </h3>
                    <p className="line-clamp-2 text-sm text-muted-foreground">
                        {session.case_title}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="rounded-full bg-secondary px-2 py-0.5 font-medium text-foreground">
                            {session.therapy_type_name}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {session.duration_minutes}m
                        </span>
                        {session.session_number_in_series > 1 && (
                            <span
                                className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-medium text-primary"
                                data-testid={`session-card-series-badge-${session.id}`}
                                title={`Session ${session.session_number_in_series} of 8 in this series`}
                            >
                                Session {session.session_number_in_series} of 8
                            </span>
                        )}
                        <span>{formatDate(session.ended_at || session.created_at)}</span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
};
